// Facility identity for the procurement PDFs (Purchase Order, GRN).
//
// These documents go to vendors, so the letterhead has to name the real facility
// rather than the placeholders HOSPITAL_INFO still ships with. The PDF builders are
// plain modules — they cannot read BrandingContext — so identity is resolved from
// sessionStorage here, exactly the way utils/reportLetterhead.js already does it for
// the lab and radiology letterheads.
//
// Anything that has no real source resolves to null and the letterhead simply omits
// it. Printing a placeholder GSTIN on a document a supplier will invoice against is
// worse than printing no GSTIN at all.
import { resolveFacilityDetails } from '../../../utils/reportLetterhead';
import { HOSPITAL_INFO } from '../constants';

// Values that exist in the codebase as filler, not as data. `resolveFacilityDetails`
// returns 'Hospital Address' / 'N/A' when it finds nothing, and HOSPITAL_INFO has
// shipped these since the module was written.
const PLACEHOLDERS = new Set([
  '00AAAAA0000A1Z0',
  'Your Hospital Address  ·  City  ·  State  ·  PIN',
  'Hospital Address',
  '+91-00000-00000',
  'N/A',
  '—',
]);

// `procurement@hlthtek.com` is the platform vendor's domain, not the hospital's.
// Printing it on a PO tells the supplier to send correspondence to Hlthtek, so it is
// treated as filler too rather than falling back to it.
const PLATFORM_EMAIL = 'procurement@hlthtek.com';

function real(value) {
  const text = String(value ?? '').trim();
  if (!text || PLACEHOLDERS.has(text)) return null;
  return text;
}

/**
 * Letterhead identity for the procurement PDFs.
 *
 * @returns {{name: string, address: string|null, gstin: string|null,
 *            pan: string|null, email: string|null}}
 *   `name` always resolves — an unnamed letterhead is worse than a generic one.
 *   Every other field is null when there is no real value behind it, and the
 *   letterhead drops the line instead of printing filler.
 */
export function resolveFacilityIdentity() {
  // Written at login by pages/application-landing/index.js and doctor/LandingPage.js.
  // Note the trap: sessionStorage `facility_name` holds the facility *id*; the display
  // name lives under `facility_display_name`.
  const sessionName = real(sessionStorage.getItem('facility_display_name'));

  // Called without userInfo: a plain module has no access to PermissionContext, so
  // this takes the sessionStorage path (facilityAddressLine1..3) written at login.
  const { address } = resolveFacilityDetails();

  const email = real(HOSPITAL_INFO.email);

  return {
    name: sessionName || HOSPITAL_INFO.name,
    address: real(address),
    // No backend field exposes these today, so both are null until an operator puts
    // real values into HOSPITAL_INFO. The placeholder GSTIN is filtered out above.
    gstin: real(HOSPITAL_INFO.gstin),
    pan: real(HOSPITAL_INFO.pan),
    email: email === PLATFORM_EMAIL ? null : email,
  };
}

/**
 * The `GSTIN: … · PAN: … · email` line under the address.
 *
 * Built with join so a missing middle field can never leave a dangling separator,
 * and returns '' when nothing resolves so the caller can skip the line and its
 * vertical advance entirely.
 */
export function formatRegistrationLine(identity) {
  return [
    identity.gstin && `GSTIN: ${identity.gstin}`,
    identity.pan && `PAN: ${identity.pan}`,
    identity.email,
  ]
    .filter(Boolean)
    .join('  ·  ');
}
